import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, Gavel, Clock, Trophy, DollarSign, Eye, X, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const iconMap = {
  outbid: Gavel,
  auction_ending: Clock,
  auction_won: Trophy,
  payment_due: DollarSign,
  auction_watched: Eye,
  auction_delivery_reported: Truck,
};

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const ref = useRef(null);

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get("/notifications/unread-count");
      setUnreadCount(res.data.unread_count);
    } catch (err) { logger.error(err); }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications?limit=15");
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count);
    } catch (err) { logger.error(err); } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { logger.error(err); }
  };

  const markAllRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) { logger.error(err); }
  };


  const runAction = async (notification, action) => {
    let body = {};
    if (action.id === "confirm_receipt" && !window.confirm(
      "Confirm receipt? This releases the protected seller payout."
    )) return;
    if (action.id === "open_dispute") {
      const reason = window.prompt("Briefly explain the delivery problem.");
      if (!reason) return;
      if (reason.trim().length < 10) {
        toast.error("Please provide at least 10 characters.");
        return;
      }
      body = { reason: reason.trim() };
    }
    setActionLoading(action.id);
    try {
      const endpoint = action.endpoint.replace(/^\/api/, "");
      await api.post(endpoint, body);
      toast.success(action.id === "confirm_receipt"
        ? "Receipt confirmed and seller payout released."
        : "The payout is on hold while Breedling reviews the dispute.");
      if (!notification.read) await markRead(notification.notification_id);
      await fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || "This auction action could not be completed.");
    } finally {
      setActionLoading("");
    }
  };
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#EF4444] text-white text-xs rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 md:w-96 bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-sm font-medium text-[#EDEDED]">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-[#D4AF37] hover:underline" data-testid="mark-all-read">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-[#71717A] hover:text-[#EDEDED]">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 text-[#3F3F46] mx-auto mb-2" />
                <p className="text-sm text-[#71717A]">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = iconMap[n.type] || Bell;
                const auctionId = n.data?.auction_id;
                return (
                  <div
                    key={n.notification_id}
                    className={`px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${!n.read ? "bg-[#D4AF37]/5" : ""}`}
                    onClick={() => { if (!n.read) markRead(n.notification_id); }}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        n.type === "outbid" ? "bg-[#EF4444]/10 text-[#EF4444]" :
                        n.type === "auction_won" ? "bg-[#22C55E]/10 text-[#22C55E]" :
                        n.type === "auction_ending" ? "bg-[#F59E0B]/10 text-[#F59E0B]" :
                        "bg-[#D4AF37]/10 text-[#D4AF37]"
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-[#EDEDED] truncate">{n.title}</p>
                          {!n.read && <span className="w-2 h-2 bg-[#D4AF37] rounded-full flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-[#71717A] mt-0.5 line-clamp-2">{n.message}</p>
                        {!!n.data?.actions?.length && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {n.data.actions.map((action) => (
                              <button
                                key={action.id}
                                type="button"
                                disabled={!!actionLoading}
                                onClick={(e) => { e.stopPropagation(); runAction(n, action); }}
                                className={`rounded-full px-3 py-1 text-xs disabled:opacity-50 ${
                                  action.id === "confirm_receipt"
                                    ? "bg-[#22C55E] text-black"
                                    : "border border-red-500/50 text-red-300"
                                }`}
                              >
                                {actionLoading === action.id ? "Working…" : action.label}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-[#52525B]">{timeAgo(n.created_at)}</span>
                          {auctionId && (
                            <Link
                              to={`/auctions/${auctionId}`}
                              className="text-xs text-[#D4AF37] hover:underline"
                              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                            >
                              View
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
